import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, PenTool, Clapperboard, Sparkles, Megaphone, Target } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const SERVICES = [
  {
    id: "website",
    icon: Globe,
    title: "Website Development",
    description: "Custom, mobile-first websites engineered to load fast, look elite, and turn visitors into clients. Our core craft.",
    featured: true,
  },
  {
    id: "graphic-design",
    icon: PenTool,
    title: "Graphic Design",
    description: "Logos, brand identities, and marketing assets with a premium finish.",
  },
  {
    id: "video-editing",
    icon: Clapperboard,
    title: "Video Editing",
    description: "Commercial-grade edits, reels, and promos that hold attention.",
  },
  {
    id: "ai-video",
    icon: Sparkles,
    title: "AI Video Creation",
    description: "Cinematic AI-generated video content — cutting-edge, on brand.",
  },
  {
    id: "social-media",
    icon: Megaphone,
    title: "Social Media Management",
    description: "Consistent, on-brand presence that grows your audience.",
  },
  {
    id: "meta-marketing",
    icon: Target,
    title: "Meta Marketing",
    description: "Targeted Facebook & Instagram ad campaigns built for ROI.",
  },
];

const flip = {
  enter: (dir) => ({ rotateY: dir > 0 ? 90 : -90, opacity: 0, scale: 0.94 }),
  center: { rotateY: 0, opacity: 1, scale: 1 },
  exit: (dir) => ({ rotateY: dir > 0 ? -90 : 90, opacity: 0, scale: 0.94 }),
};

export function Services() {
  const [[index, direction], setState] = useState([0, 0]);
  const paginate = (dir) => setState(([i]) => [(i + dir + SERVICES.length) % SERVICES.length, dir]);
  const service = SERVICES[index];

  return (
    <section id="services" data-testid="services-section" className="px-4 sm:px-6 lg:px-12 py-10 md:py-12 bg-black">
      <div className="max-w-7xl mx-auto lg:px-14">
        <Reveal className="mb-14 md:mb-16 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-medium mb-4">services</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl mx-auto">
            No Limit to <span className="text-[#D4AF37]">Creation</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative max-w-2xl mx-auto px-12 sm:px-16" data-testid="services-deck">
            <button onClick={() => paginate(-1)} data-testid="services-prev-btn" aria-label="Previous service"
              className="absolute left-3 sm:left-0 top-1/2 -translate-y-1/2 z-10 text-slate-500 hover:text-amber-300 hover:[filter:drop-shadow(0_0_12px_rgba(212,175,55,0.5))] transition-all duration-300">
              <span className="font-serif text-4xl font-bold leading-none">❮</span>
            </button>
            <button onClick={() => paginate(1)} data-testid="services-next-btn" aria-label="Next service"
              className="absolute right-3 sm:right-0 top-1/2 -translate-y-1/2 z-10 text-slate-500 hover:text-amber-300 hover:[filter:drop-shadow(0_0_12px_rgba(212,175,55,0.5))] transition-all duration-300">
              <span className="font-serif text-4xl font-bold leading-none">❯</span>
            </button>

            <div className="relative h-[320px] sm:h-[280px]" style={{ perspective: 1200 }}>
              <div aria-hidden className="absolute inset-0 notched border border-white/5 bg-[#0E1017] translate-y-5 scale-[0.94]" />
              <div aria-hidden className="absolute inset-0 notched border border-white/10 bg-[#10121A] translate-y-2.5 scale-[0.97]" />
              <AnimatePresence custom={direction} mode="popLayout" initial={false}>
                <motion.div
                  key={index}
                  custom={direction}
                  variants={flip}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  style={{ transformStyle: "preserve-3d" }}
                  data-testid={`service-card-${service.id}`}
                  className={`absolute inset-0 notched p-8 sm:p-10 flex flex-col justify-center text-center sm:text-left ${
                    service.featured
                      ? "border border-amber-400/40 bg-gradient-to-br from-[#1A1D28] to-[#12141C]"
                      : "border border-amber-500/20 bg-[#12141C]"
                  }`}
                >
                  <div className={`h-12 w-12 notched-sm flex items-center justify-center mx-auto sm:mx-0 mb-6 ${
                    service.featured ? "bg-amber-400/15 border border-amber-400/30" : "bg-white/5 border border-white/10"
                  }`}>
                    <service.icon className={`h-5 w-5 ${service.featured ? "text-amber-300" : "text-slate-300"}`} />
                  </div>
                  <h3 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight flex items-center justify-center sm:justify-start gap-3 flex-wrap">
                    {service.title}
                    {service.featured && (
                      <span className="text-[10px] font-body font-bold uppercase tracking-[0.15em] text-black bg-amber-400 notched-sm px-2.5 py-1">Main</span>
                    )}
                  </h3>
                  <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed max-w-md mx-auto sm:mx-0">{service.description}</p>
                </motion.div>
              </AnimatePresence>
            </div>

            <p className="mt-6 text-center text-xs tracking-[0.25em] text-slate-500" data-testid="services-counter">
              {String(index + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
